import { FlowRun } from "@/models/flows";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "../../Modal";
import { FlowRunDetail } from "../../flows/flow-runs/FlowRunDetail";
import { flow } from "lodash";
import { ViewResponseDialog } from "../../forms/ViewResponseDialog";
import { useQuery } from "react-query";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { ButtonSpinner } from "@/components/ButtonSpinner";
import { useDarkMode } from "@/hooks";
import { StatusBadge } from "../../flows/StatusBadge";

interface Props {
  flowRun: FlowRun;
}

export const CreatedFromFlowRun = ({ flowRun }: Props) => {
  const [showFlowRun, setShowFlowRun] = useState(false);
  const [showFormResponse, setShowFormResponse] = useState(false);
  const darkMode = useDarkMode();

  const {
    data: form,
    isLoading,
    isError,
  } = useQuery(
    ["form", flowRun?.triggerInput?.formId],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/dashboard/forms/${flowRun?.triggerInput?.formId}`
      );
      return response.data;
    },
    {
      enabled:
        !!(flowRun.trigger.triggerType === "form_submitted") &&
        !!flowRun.triggerInput.formId, // Only fetch if it was triggered by a form submission and we have a formId
    }
  );

  const {
    data: formResponse,
    isLoading: formResponseIsLoading,
    isError: formResponseError,
  } = useQuery(
    ["formResponse", flowRun?.triggerInput?.responseId],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/dashboard/forms/${form.id}/responses/${flowRun?.triggerInput?.responseId}`
      );
      return response.data;
    },
    {
      enabled: !!flowRun?.triggerInput?.responseId && !!form, // Only fetch if formId exists
    }
  );

  return (
    <div className="w-full p-6 space-y-6">
      <Modal isOpen={showFlowRun} setIsOpen={setShowFlowRun} className="w-1/2">
        <div className="min-w-3xl w-full">
          <h3 className="dark:text-white text-lg mb-4">
            Flow run for{" "}
            <span className="font-semibold">{flowRun.displayName}</span>
          </h3>
          <FlowRunDetail flowRun={flowRun} />
        </div>
      </Modal>

      {form && formResponse && (
        <ViewResponseDialog
          open={showFormResponse}
          setOpen={setShowFormResponse}
          form={form}
          formResponse={formResponse}
        />
      )}

      <h2 className="text-xl font-semibold dark:text-white">
        Created from Flow Run
      </h2>
      <div
        className={`grid grid-cols-2 gap-y-4 text-sm text-muted-foreground `}
      >
        <div>Flow Run</div>
        <div className="text-right font-medium text-foreground dark:text-white flex justify-end">
          <button
            onClick={() => setShowFlowRun(true)}
            className="ml-1 text-zinc-500 hover:text-zinc-600 dark:text-zinc-300 dark:hover:text-zinc-600 transition"
          >
            {flowRun.flow.name} - #{flowRun.id} (Click to view)
          </button>
        </div>
        <div>Status</div>
        <div className="text-right font-medium text-foreground dark:text-white flex justify-end">
          <StatusBadge status={flowRun.status} />
        </div>
        <div>Triggered by</div>
        <div className="text-right font-medium text-foreground dark:text-white justify-end flex items-center">
          {flowRun.trigger.triggerType === "form_submitted" && (
            <div className="flex">
              <div className="inline-flex items-center rounded-md bg-blue-400/10 px-2 py-1 text-xs font-medium text-blue-400 ring-1 ring-inset ring-blue-400/30">
                Form submitted (Response #{flowRun?.triggerInput?.responseId})
              </div>
            </div>
          )}

          {flowRun.trigger.triggerType === "manual" && <>Manually started</>}
        </div>

        {flowRun.trigger.triggerType === "form_submitted" && (
          <>
            <div>Response</div>
            <div className="text-right font-medium text-foreground dark:text-white justify-end flex items-center">
              {form && formResponse ? (
                <button
                  onClick={() => setShowFormResponse(true)}
                  className="ml-1 text-zinc-500 hover:text-zinc-600 dark:text-zinc-300 dark:hover:text-zinc-600 transition"
                >
                  (View response)
                </button>
              ) : (
                <div className="flex items-center flex-nowrap">
                  <ButtonSpinner colour={darkMode ? "white" : "black"} />
                  <div>
                    <span className="ml-2">Loading response...</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
